export interface TransformationProofContent {
  eyebrow: string;
  title: string;
  description: string;
  supporting: string;
  stat: string;
  proofPoints: Array<{
    label: string;
    value: string;
  }>;
  slider: {
    beforeSrc: string;
    afterSrc: string;
    beforeAlt: string;
    afterAlt: string;
    beforeLabel: string;
    afterLabel: string;
  };
}

export interface ListingProofContent {
  eyebrow: string;
  title: string;
  description: string;
  metrics: string[];
  supportingPoints: string[];
  mainCard: {
    title: string;
    subtitle: string;
    image: string;
    badges: string[];
  };
  comparisonCard: {
    title: string;
    subtitle: string;
    image: string;
  };
  socialCard: {
    title: string;
    subtitle: string;
    image: string;
  };
}

export interface StyleBreadthContent {
  eyebrow: string;
  title: string;
  description: string;
  proofTags: string[];
  styles: Array<{
    name: string;
    audience: string;
    image: string;
    accentClass: string;
  }>;
}

export interface WorkflowProofContent {
  eyebrow: string;
  title: string;
  description: string;
  uploadImage: string;
  uploadTags: string[];
  selectorOptions: Array<{
    name: string;
    image: string;
    selected?: boolean;
  }>;
  finalImage: string;
  exportTags: string[];
}

export const transformationProofContent: TransformationProofContent = {
  eyebrow: 'Transformation Proof',
  title: 'Everyday photos show furniture. Staged photos sell possibility.',
  description:
    'The room does not change. The architecture does not change. What changes is how clearly the buyer can picture a better life inside it.',
  supporting: 'Drag the same room from lived-in to staged and watch the listing sharpen.',
  stat: '30-second transformation',
  proofPoints: [
    {
      label: 'Same room',
      value: 'No camera tricks or remodel required',
    },
    {
      label: 'Same daylight',
      value: 'The story changes, not the architecture',
    },
    {
      label: 'Better first impression',
      value: 'Built to win the MLS thumbnail',
    },
  ],
  slider: {
    beforeSrc: '/images/styles-page/coastal_staging_style_lived_in.webp',
    afterSrc: '/images/styles-page/coastal_staging_style.webp',
    beforeAlt: 'Lived-in bedroom listing photo',
    afterAlt: 'Coastal staged bedroom listing photo',
    beforeLabel: 'Lived-in',
    afterLabel: 'Staged',
  },
};

export const listingProofContent: ListingProofContent = {
  eyebrow: 'Visibility Proof',
  title: 'Win the thumbnail battle before buyers read a word.',
  description:
    'The lead image has to carry the listing everywhere it appears: MLS grids, social posts, email roundups, and brokerage recaps. This layout shows the staged hero doing the heavy lifting.',
  metrics: ['MLS-ready image set', '21 design styles', 'Scroll-stopping lead photo'],
  supportingPoints: [
    'One polished hero image for the listing grid',
    'Mobile crops that still hold attention',
    'Consistent quality across every marketing surface',
  ],
  mainCard: {
    title: 'Featured hero',
    subtitle: 'The image buyers stop on first',
    image: '/images/styles-page/luxury_staging_style.webp',
    badges: ['Staged', 'MLS-ready', 'Lead photo'],
  },
  comparisonCard: {
    title: 'Vacant thumbnail',
    subtitle: 'Technically accurate, emotionally flat',
    image: '/images/styles-page/no_furniture_empty_room.webp',
  },
  socialCard: {
    title: 'Mobile crop',
    subtitle: 'Still crisp when the feed gets crowded',
    image: '/images/styles-page/coastal_staging_style.webp',
  },
};

export const styleBreadthContent: StyleBreadthContent = {
  eyebrow: 'Style Range Proof',
  title: 'One room. Four buyer stories.',
  description:
    'Start with the same architecture, then tune the emotional signal for the audience you want to move. No reshoot. No redesign project. Just a better fit for the buyer.',
  proofTags: ['Same architecture', 'Controlled variation', 'Audience-fit styling'],
  styles: [
    {
      name: 'Coastal',
      audience: 'Second-home and sunbelt buyers',
      image: '/images/styles-page/empty_room_proof_coastal.webp',
      accentClass: 'bg-[#e3efe9] text-[#355f56]',
    },
    {
      name: 'Modern',
      audience: 'Urban professionals and new construction',
      image: '/images/styles-page/empty_room_proof_modern.webp',
      accentClass: 'bg-[#ebe6de] text-[#3f3a36]',
    },
    {
      name: 'Traditional',
      audience: 'Move-up families and classic homes',
      image: '/images/styles-page/empty_room_proof_traditional.webp',
      accentClass: 'bg-[#efe3da] text-[#6a4335]',
    },
    {
      name: 'Scandinavian',
      audience: 'Design-savvy buyers who want calm',
      image: '/images/styles-page/empty_room_proof_scandinavian.webp',
      accentClass: 'bg-[#edf1ea] text-[#54624a]',
    },
  ],
};

export const workflowProofContent: WorkflowProofContent = {
  eyebrow: 'Speed Proof',
  title: 'From photo to MLS in 30 seconds.',
  description:
    'The workflow needs to feel obvious: upload the original, choose the style direction, and export a finished marketing asset without sending the listing into a multi-day staging loop.',
  uploadImage: '/images/styles-page/no_furniture_empty_room.webp',
  uploadTags: ['iPhone photo', 'No reshoot', 'Drag and drop'],
  selectorOptions: [
    {
      name: 'Coastal',
      image: '/images/styles-page/empty_room_proof_coastal.webp',
    },
    {
      name: 'Modern',
      image: '/images/styles-page/empty_room_proof_modern.webp',
      selected: true,
    },
    {
      name: 'Traditional',
      image: '/images/styles-page/empty_room_proof_traditional.webp',
    },
    {
      name: 'Scandinavian',
      image: '/images/styles-page/empty_room_proof_scandinavian.webp',
    },
  ],
  finalImage: '/images/styles-page/empty_room_proof_modern.webp',
  exportTags: ['HD', '4K', 'MLS-ready', 'Download'],
};
